import { Completion, CompletionContext, CompletionResult } from "@codemirror/autocomplete"
import { getJSONSchema } from "@spglobal-pgm/codemirror-json-schema"
import { yamlCompletion } from "@spglobal-pgm/codemirror-json-schema/yaml"
import { parse } from "yaml"

import { AppNode } from "@features/builder/graph/types"
import { fixNodesCompletion } from "@features/builder/DashboardConfiguration/YamlEditor/fixNodesCompletion"
import { shouldForceNestedKeyCompletion } from "@features/builder/DashboardConfiguration/YamlEditor/fixNestedKeyCompletion"

/**
 * Creates a completion override function for YAML editor that:
 * 1. First tries custom nodes completion fix (for Nodes arrays)
 * 2. Then falls back to schema-based completion from the library
 * 3. Filters out empty/invalid results from the library
 * 4. Returns null to allow other completion sources if nothing matches
 */
export function createYamlCompletionOverride(nodes: AppNode[]) {
  // CRITICAL: Create the yamlCompletion function ONCE and reuse it.
  // yamlCompletion() creates a JSONCompletion instance that needs to access
  // the schema from CodeMirror state. Creating new instances on each call
  // means the schema is never loaded from state.
  const yamlCompletionFn = yamlCompletion()

  const normalizeCompletion = (
    result: CompletionResult | Completion[] | null,
    from: number,
  ): CompletionResult | null => {
    if (!result) {
      return null
    }

    if (Array.isArray(result)) {
      return result.length > 0 ? { from, options: result } : null
    }

    return result
  }

  const getRootSchemaCompletions = (context: CompletionContext): CompletionResult | null => {
    const schema = getJSONSchema(context.state)

    if (!schema || typeof schema !== "object" || Array.isArray(schema)) {
      return null
    }

    const properties = schema.properties
    if (!properties || typeof properties !== "object" || Array.isArray(properties)) {
      return null
    }

    const required = Array.isArray(schema.required) ? schema.required : []
    const optionalObjectKeys = Object.entries(properties).reduce<string[]>(
      (result, [key, value]) => {
        if (!value || typeof value !== "object" || Array.isArray(value)) {
          return result
        }

        const schemaType = value.type
        if (
          schemaType === "object" ||
          (Array.isArray(schemaType) && schemaType.includes("object"))
        ) {
          return [...result, key]
        }

        return result
      },
      [],
    )

    const baseKeys = new Set([...required, ...optionalObjectKeys])

    let existingKeys: string[] = []
    try {
      const parsed = parse(context.state.doc.toString())
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        existingKeys = Object.keys(parsed)
      }
    } catch (error) {
      existingKeys = []
    }

    const options: Completion[] = []
    for (const key of baseKeys) {
      if (existingKeys.includes(key)) {
        continue
      }

      if (Object.prototype.hasOwnProperty.call(properties, key)) {
        options.push({
          label: key,
          type: "property",
          apply: `${key}: `,
        })
      }
    }

    if (options.length === 0) {
      return null
    }

    return { from: context.pos, options }
  }

  const getSchemaProperty = (schema: unknown, key: string): unknown => {
    if (!schema || typeof schema !== "object" || Array.isArray(schema)) {
      return null
    }

    const typedSchema = schema as {
      properties?: Record<string, unknown>
      oneOf?: unknown[]
      anyOf?: unknown[]
      allOf?: unknown[]
    }
    const properties = typedSchema.properties
    if (properties && typeof properties === "object" && !Array.isArray(properties)) {
      if (Object.prototype.hasOwnProperty.call(properties, key)) {
        return properties[key]
      }
    }

    const variants = typedSchema.oneOf || typedSchema.anyOf || typedSchema.allOf
    if (Array.isArray(variants)) {
      for (const variant of variants) {
        const found = getSchemaProperty(variant, key)
        if (found) {
          return found
        }
      }
    }

    return null
  }

  const getArrayItemSchema = (schema: unknown): unknown => {
    if (!schema || typeof schema !== "object" || Array.isArray(schema)) {
      return null
    }

    const typedSchema = schema as { items?: unknown; type?: string | string[] }
    const items = typedSchema.items
    if (Array.isArray(items)) {
      return items[0] ?? null
    }

    if (items && typeof items === "object") {
      return items
    }

    const schemaType = typedSchema.type
    if (schemaType === "array" || (Array.isArray(schemaType) && schemaType.includes("array"))) {
      return null
    }

    return null
  }

  const getSchemaProperties = (schema: unknown): Record<string, unknown> => {
    if (!schema || typeof schema !== "object" || Array.isArray(schema)) {
      return {}
    }

    const typedSchema = schema as {
      properties?: Record<string, unknown>
      oneOf?: unknown[]
      anyOf?: unknown[]
      allOf?: unknown[]
      then?: unknown
      else?: unknown
    }
    const properties = typedSchema.properties
    const baseProperties =
      properties && typeof properties === "object" && !Array.isArray(properties) ? properties : {}

    const variants = typedSchema.oneOf || typedSchema.anyOf || typedSchema.allOf
    if (Array.isArray(variants)) {
      return variants.reduce<Record<string, unknown>>((result, variant) => {
        const variantProps = getSchemaProperties(variant)
        return { ...result, ...variantProps }
      }, baseProperties)
    }

    const thenProps = typedSchema.then ? getSchemaProperties(typedSchema.then) : {}
    const elseProps = typedSchema.else ? getSchemaProperties(typedSchema.else) : {}

    return { ...baseProperties, ...thenProps, ...elseProps }
  }

  const collectSchemaValuesForProperty = (schema: unknown, key: string): string[] => {
    const values = new Set<string>()

    const collectFromPropertySchema = (propertySchema: unknown) => {
      if (!propertySchema || typeof propertySchema !== "object" || Array.isArray(propertySchema)) {
        return
      }

      const typedSchema = propertySchema as {
        enum?: unknown[]
        const?: unknown
        oneOf?: unknown[]
        anyOf?: unknown[]
        allOf?: unknown[]
      }

      if (Array.isArray(typedSchema.enum)) {
        for (const value of typedSchema.enum) {
          if (value !== undefined && value !== null) {
            values.add(value.toString())
          }
        }
      }

      if (typeof typedSchema.const !== "undefined") {
        values.add(typedSchema.const?.toString() ?? "")
      }

      const variants = typedSchema.oneOf || typedSchema.anyOf || typedSchema.allOf
      if (Array.isArray(variants)) {
        for (const variant of variants) {
          collectFromPropertySchema(variant)
        }
      }
    }

    const collectFromSchema = (schema: unknown) => {
      if (!schema || typeof schema !== "object" || Array.isArray(schema)) {
        return
      }

      const typedSchema = schema as {
        properties?: Record<string, unknown>
        oneOf?: unknown[]
        anyOf?: unknown[]
        allOf?: unknown[]
      }

      const properties = typedSchema.properties
      if (properties && Object.prototype.hasOwnProperty.call(properties, key)) {
        collectFromPropertySchema(properties[key])
      }

      const variants = typedSchema.oneOf || typedSchema.anyOf || typedSchema.allOf
      if (Array.isArray(variants)) {
        for (const variant of variants) {
          collectFromSchema(variant)
        }
      }
    }

    collectFromSchema(schema)

    return Array.from(values).filter(Boolean)
  }

  const getListItemPath = (context: CompletionContext, currentIndent: number): string[] => {
    const path: string[] = []
    const line = context.state.doc.lineAt(context.pos)

    for (let lineNumber = line.number - 1; lineNumber >= 1; lineNumber -= 1) {
      const previousLine = context.state.doc.line(lineNumber)
      const previousText = previousLine.text

      if (!previousText.trim()) {
        continue
      }

      const previousIndent = previousText.match(/^\s*/)?.[0].length ?? 0
      if (previousIndent < currentIndent) {
        const trimmed = previousText.trim()
        if (!trimmed.startsWith("-")) {
          const keyMatch = trimmed.match(/^([^:#]+):/)
          if (keyMatch) {
            path.unshift(keyMatch[1]?.trim() ?? "")
            currentIndent = previousIndent
          }
        }
      }
    }

    return path.filter(Boolean)
  }

  const getListItemSchemaCompletions = (context: CompletionContext): CompletionResult | null => {
    const line = context.state.doc.lineAt(context.pos)
    const beforeCursor = line.text.slice(0, context.pos - line.from)
    if (beforeCursor.includes(":")) {
      return null
    }

    const currentIndent = beforeCursor.match(/^\s*/)?.[0].length ?? 0
    const listMatch = beforeCursor.match(/^\s*-\s*([^\s:]*)$/)

    let currentWord = ""
    let isListContext = Boolean(listMatch)
    if (listMatch) {
      currentWord = listMatch[1] ?? ""
    } else if (!beforeCursor.trim()) {
      for (let lineNumber = line.number - 1; lineNumber >= 1; lineNumber -= 1) {
        const previousLine = context.state.doc.line(lineNumber)
        const previousText = previousLine.text

        if (!previousText.trim()) {
          continue
        }

        const previousIndent = previousText.match(/^\s*/)?.[0].length ?? 0
        if (previousIndent < currentIndent && previousText.trim().startsWith("-")) {
          isListContext = true
          break
        }

        if (previousIndent < currentIndent && !previousText.trim().startsWith("-")) {
          break
        }
      }
    }

    if (!isListContext) {
      return null
    }

    const path = getListItemPath(context, currentIndent)
    if (path.length === 0) {
      return null
    }

    let schema: unknown = getJSONSchema(context.state)
    for (const key of path) {
      const propertySchema = getSchemaProperty(schema, key)
      if (!propertySchema) {
        return null
      }

      schema = propertySchema
      const itemSchema = getArrayItemSchema(schema)
      if (itemSchema) {
        schema = itemSchema
      }
    }

    const properties = getSchemaProperties(schema)
    const options = Object.keys(properties)
      .filter((key) => key.toLowerCase().startsWith(currentWord.toLowerCase()))
      .map((key) => ({
        label: key,
        type: "property",
        apply: `${key}: `,
      }))

    if (options.length === 0) {
      return null
    }

    const from = context.pos - currentWord.length
    return { from, options }
  }

  const getPropertyValueSchemaCompletions = (
    context: CompletionContext,
  ): CompletionResult | null => {
    const line = context.state.doc.lineAt(context.pos)
    const beforeCursor = line.text.slice(0, context.pos - line.from)
    const match = beforeCursor.match(/^\s*-?\s*([^:#]+):\s*([^\s#]*)$/)
    if (!match) {
      return null
    }

    const key = match[1]?.trim() ?? ""
    const currentWord = match[2] ?? ""
    if (!key) {
      return null
    }

    const currentIndent = beforeCursor.match(/^\s*/)?.[0].length ?? 0
    const path = getListItemPath(context, currentIndent)

    let schema: unknown = getJSONSchema(context.state)
    for (const pathKey of path) {
      const propertySchema = getSchemaProperty(schema, pathKey)
      if (!propertySchema) {
        return null
      }

      schema = propertySchema
      const itemSchema = getArrayItemSchema(schema)
      if (itemSchema) {
        schema = itemSchema
      }
    }

    const values = collectSchemaValuesForProperty(schema, key).filter((value) =>
      value.toLowerCase().startsWith(currentWord.toLowerCase()),
    )

    if (values.length === 0) {
      return null
    }

    const from = context.pos - currentWord.length
    const options = values.map((value) => ({
      label: value,
      type: "text",
      apply: value,
    }))

    return { from, options }
  }

  return (context: CompletionContext): CompletionResult | null => {
    // codemirror-json-schema have a few problems. Apply fixes first
    // Patch nodes completion
    const nodesCompletion = fixNodesCompletion(context, nodes)
    if (nodesCompletion) {
      return nodesCompletion
    }

    // Schema completion provided by the lib
    // Use the SAME instance we created above
    const schemaResult = normalizeCompletion(yamlCompletionFn(context), context.pos)
    if (schemaResult && "from" in schemaResult && schemaResult.options.length > 0) {
      return schemaResult
    }

    if (context.explicit) {
      const valueResult = getPropertyValueSchemaCompletions(context)
      if (valueResult) {
        return valueResult
      }

      const listItemResult = getListItemSchemaCompletions(context)
      if (listItemResult) {
        return listItemResult
      }
    }

    if (context.explicit) {
      const line = context.state.doc.lineAt(context.pos)
      const indent = line.text.match(/^\s*/)?.[0].length ?? 0
      if (line.text.trim().length === 0 && indent === 0) {
        const rootFallback = getRootSchemaCompletions(context)
        if (rootFallback) {
          return rootFallback
        }
      }
    }

    if (shouldForceNestedKeyCompletion(context)) {
      const tempState = context.state.update({
        changes: { from: context.pos, to: context.pos, insert: ":" },
      }).state
      const forcedContext = new CompletionContext(tempState, context.pos, true)
      const forcedResult = normalizeCompletion(yamlCompletionFn(forcedContext), forcedContext.pos)

      if (forcedResult && "from" in forcedResult && forcedResult.options.length > 0) {
        return forcedResult
      }
    }

    return null
  }
}
